import type { BoardDifficulty } from '@/features/live-engine/board-pool';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface DifficultySelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (difficulty: BoardDifficulty) => void;
  accentColor?: string;
}

const DIFFICULTIES: { id: BoardDifficulty; labelKey: string }[] = [
  { id: 'easy', labelKey: 'difficulty.easy' },
  { id: 'medium', labelKey: 'difficulty.medium' },
  { id: 'hard', labelKey: 'difficulty.hard' },
];

export function DifficultySelectModal({
  isOpen,
  onClose,
  onSelect,
  accentColor = '#25B19A',
}: Readonly<DifficultySelectModalProps>) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-5 bg-black/55 backdrop-blur-[3px]"
      role="dialog"
      aria-modal="true"
      aria-label={t('difficulty.title')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative max-w-[400px] w-full overflow-hidden rounded-[18px] border !border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.55)]"
        style={{
          background:
            'linear-gradient(180deg, color-mix(in srgb, var(--game-eclipse, #205A52) 40%, var(--game-bg, #0D2320)), var(--game-bg, #0D2320))',
        }}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" />

        <div className="flex flex-col gap-4 p-5">
          <div className="!space-y-[6px] mb-[15px]">
            <p className="cardDescription mt-0">{t('difficulty.kicker')}</p>
            <h3 className="cardTitle">{t('difficulty.title')}</h3>
            <p className="cardDescription">{t('difficulty.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 gap-[15px]">
            {DIFFICULTIES.map((d) => (
              <button
                key={d.id}
                type="button"
                className="flex items-center justify-between rounded-[14px] border !border-white/15 bg-white/5 backdrop-blur-[40px] px-4 py-3 text-left text-white transition hover:bg-white/10"
                onClick={() => {
                  onSelect(d.id);
                }}
              >
                <span className="text-[14px] font-semibold tracking-[0.08em] uppercase">
                  {t(d.labelKey)}
                </span>
                <span className="text-white/50" aria-hidden style={{ color: accentColor }}>
                  <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      opacity="0.5"
                      d="M8.51113 0.319767C8.71594 0.11502 8.99368 0 9.28328 0C9.57288 0 9.85062 0.11502 10.0554 0.319767L14.9701 5.23444C15.1749 5.43925 15.2899 5.71699 15.2899 6.00659C15.2899 6.29619 15.1749 6.57394 14.9701 6.77874L10.0554 11.6934C9.84945 11.8924 9.57357 12.0024 9.28721 12C9.00085 11.9975 8.72692 11.8826 8.52443 11.6801C8.32194 11.4776 8.20708 11.2037 8.20459 10.9173C8.2021 10.631 8.31218 10.3551 8.51113 10.1491L11.4676 7.09874H1.09215C0.802494 7.09874 0.524701 6.98368 0.319883 6.77886C0.115066 6.57404 0 6.29625 0 6.00659C0 5.71694 0.115066 5.43914 0.319883 5.23433C0.524701 5.02951 0.802494 4.91444 1.09215 4.91444H11.4676L8.51113 1.86407C8.30638 1.65926 8.19136 1.38152 8.19136 1.09192C8.19136 0.802318 8.30638 0.524575 8.51113 0.319767Z"
                      fill="currentColor"
                      fillOpacity="0.5"
                    />
                  </svg>
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="mt-4 rounded-[14px] border !border-white/15 bg-white/5 backdrop-blur-[40px] px-4 py-3 text-center text-white transition hover:bg-white/10"
            onClick={onClose}
          >
            {t('difficulty.cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
